
import * as React from 'react';
import type { ViewType, User, Opportunity, Application, Band, Lineup } from '../types';
import { useAuth } from './AuthProvider';
import * as api from '../services/apiService';
import { useUI } from './UIProvider';

const { createContext, useState, useCallback, useMemo, useContext, useEffect, useRef } = React;

type NetworkPageTab = 'endorsing' | 'followers' | 'preferences' | 'security';

export interface NavigationContextState {
  view: ViewType;
  isMobileMenuOpen: boolean;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  viewingProfile: User | null;
  viewingOpportunity: Opportunity | null;
  setViewingOpportunity: React.Dispatch<React.SetStateAction<Opportunity | null>>;
  viewingApplication: Application | null;
  viewingBand: Band | null;
  viewingLineup: Partial<Lineup> | null;
  viewingFanListOwner: User | Band | null;
  viewingEndorserListOwner: User | null;
  bands: Band[];
  networkTab: NetworkPageTab | null;
  navigationOrigin: ViewType | null;
  setIsMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleNavigation: (targetView: ViewType, payload?: any) => void;
  handleViewProfile: (user: User) => void;
  handleViewOpportunity: (opportunity: Opportunity) => void;
  handleViewApplication: (application: Application, opportunity: Opportunity) => void;
  handleViewApplicantsForOpportunity: (opportunity: Opportunity) => void;
  handleEditOpportunity: (opportunity: Opportunity) => void;
  handleViewBand: (band: Band) => void;
  handleViewFanList: (owner: User | Band) => void;
  handleViewEndorserList: (owner: User) => void;
  handleClaimBand: (bandId: string) => Promise<void>;
  handleToggleBandFan: (bandId: string) => Promise<void>;
  hasMoreBands: boolean;
  isLoadingMoreBands: boolean;
  loadMoreBands: () => Promise<void>;
  refreshBands: () => Promise<void>;
  handleViewLineupEditor: (opportunity: Opportunity) => void;
  handleViewFlyer: (lineup: Partial<Lineup>, opportunity: Opportunity) => void;
}

const NavigationContext = createContext<NavigationContextState | null>(null);

export const useNavigation = (): NavigationContextState => {
    const context = useContext(NavigationContext);
    if (!context) throw new Error('useNavigation must be used within a NavigationProvider');
    return context;
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { authStatus } = useAuth();
    const { showToast } = useUI();

    const [view, setView] = useState<ViewType>('feed');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewingProfile, setViewingProfile] = useState<User | null>(null);
    const [viewingOpportunity, setViewingOpportunity] = useState<Opportunity | null>(null);
    const [viewingApplication, setViewingApplication] = useState<Application | null>(null);
    const [viewingBand, setViewingBand] = useState<Band | null>(null);
    const [viewingLineup, setViewingLineup] = useState<Partial<Lineup> | null>(null);
    const [viewingFanListOwner, setViewingFanListOwner] = useState<User | Band | null>(null);
    const [viewingEndorserListOwner, setViewingEndorserListOwner] = useState<User | null>(null);
    const [bands, setBands] = useState<Band[]>([]);
    const [hasMoreBands, setHasMoreBands] = useState(false);
    const [isLoadingMoreBands, setIsLoadingMoreBands] = useState(false);
    const lastBandDocIdRef = useRef<string | null>(null);
    const [networkTab, setNetworkTab] = useState<NetworkPageTab | null>(null);
    const [navigationOrigin, setNavigationOrigin] = useState<ViewType | null>(null);

    const refreshBands = useCallback(async () => {
        if (authStatus === 'authenticated') {
            try {
                lastBandDocIdRef.current = null;
                const result = await api.getBands();
                setBands(result.bands);
                lastBandDocIdRef.current = result.lastDocId;
                setHasMoreBands(result.hasMore);
            } catch (error) {
                console.error("Failed to refresh bands:", error);
            }
        }
    }, [authStatus]);

    const loadMoreBands = useCallback(async () => {
        if (isLoadingMoreBands || !hasMoreBands) return;
        setIsLoadingMoreBands(true);
        try {
            const result = await api.getBands(lastBandDocIdRef.current || undefined);
            setBands(prev => {
                const existingIds = new Set(prev.map(b => b.id));
                const uniqueNew = result.bands.filter(b => !existingIds.has(b.id));
                return [...prev, ...uniqueNew];
            });
            lastBandDocIdRef.current = result.lastDocId;
            setHasMoreBands(result.hasMore);
        } catch (error) {
            console.error("Failed to load more bands", error);
        } finally {
            setIsLoadingMoreBands(false);
        }
    }, [isLoadingMoreBands, hasMoreBands]);

    useEffect(() => { refreshBands(); }, [authStatus, refreshBands]);

    // --- URL Sync Logic ---
    const updateURL = useCallback((targetView: ViewType, payload: any = {}) => {
        const params = new URLSearchParams();
        params.set('v', targetView);
        if (payload.user?.handle) params.set('u', payload.user.handle);
        if (payload.band?.id) params.set('b', payload.band.id);
        if (payload.opportunity?.id) params.set('id', payload.opportunity.id);
        if (payload.session?.id) params.set('session', payload.session.id);
        if (payload.tab) params.set('tab', payload.tab);

        const newRelativePathQuery = window.location.pathname + '?' + params.toString();
        window.history.pushState({ view: targetView, payload }, '', newRelativePathQuery);
    }, []);

    const handleNavigation = useCallback((targetView: ViewType, payload: any = {}) => {
        const contextFreeViews: ViewType[] = [
            'feed', 'myActivity', 'createOpportunity', 'network', 'messages', 
            'pumpUpTheVolume', 'notifications', 'feedback', 'terms', 'privacy', 'cookies',
            'recommended', 'calendar', 'settings', 'myBands', 'myLineups', 'myFlyers', 'myProjects', 'editProfile', 'sessions'
        ];
        
        if (contextFreeViews.includes(targetView)) {
            setViewingProfile(null);
            setViewingBand(null);
            setViewingOpportunity(null);
            setViewingApplication(null);
            setViewingLineup(null);
            setViewingFanListOwner(null);
            setViewingEndorserListOwner(null);
        }

        if (payload.user) setViewingProfile(payload.user);
        if (payload.band) setViewingBand(payload.band);
        if (payload.opportunity) setViewingOpportunity(payload.opportunity);
        
        setNetworkTab(payload.tab || null);
        setNavigationOrigin(null);
        setView(targetView);
        setIsMobileMenuOpen(false);
        updateURL(targetView, payload);
    }, [updateURL]);

    // Handle deep linking and refreshes
    useEffect(() => {
        const hydrateFromURL = async () => {
            const params = new URLSearchParams(window.location.search);
            const v = params.get('v');
            const u = params.get('u');
            const id = params.get('id');
            const b = params.get('b');
            const session = params.get('session');
            const tab = params.get('tab');

            if (!v && !session && !id && !u && !b) return;

            if (session) {
                setView('sessions');
                return;
            }

            if (v === 'opp' || id) {
                const targetId = id || params.get('id');
                if (targetId) {
                    try {
                        const oppList = await api.getOpportunities(); 
                        const found = oppList.opportunities.find(o => o.id === targetId);
                        if (found) {
                            setViewingOpportunity(found);
                            setView('opportunityDetail');
                        }
                    } catch (e) {}
                }
            } else if (v === 'profile' || u) {
                if (u) {
                    try {
                        const user = await api.getUserByHandle(u);
                        setViewingProfile(user);
                        setView('profile');
                    } catch (e) {}
                }
            } else if (v === 'band' || b) {
                if (b) {
                    try {
                        const band = await api.getBandById(b);
                        setViewingBand(band);
                        setView('bandPage');
                    } catch (e) {}
                }
            } else if (v) {
                setView(v as ViewType);
                if (tab) setNetworkTab(tab as NetworkPageTab);
            }
        };

        if (authStatus === 'authenticated') {
            hydrateFromURL();
        }
    }, [authStatus]);

    // Handle Back Button
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            if (event.state && event.state.view) {
                setView(event.state.view);
                if (event.state.payload?.user) setViewingProfile(event.state.payload.user);
                if (event.state.payload?.band) setViewingBand(event.state.payload.band);
                if (event.state.payload?.opportunity) setViewingOpportunity(event.state.payload.opportunity);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);
    
    const handleViewProfile = useCallback(async (user: User) => {
        setNavigationOrigin(view);
        setViewingProfile(user);
        setView('profile');
        setIsMobileMenuOpen(false);
        updateURL('profile', { user });
        
        try {
            const fullUser = await api.getUserByHandle(user.handle);
            setViewingProfile(prev => prev && prev.handle === user.handle ? fullUser : prev);
        } catch (e) {}
        api.logProfileView(user.handle).catch(() => {});
    }, [view, updateURL]);

    const handleViewOpportunity = useCallback(async (opportunity: Opportunity) => { 
        setNavigationOrigin(view); 
        setViewingOpportunity(opportunity); 
        setView('opportunityDetail');
        updateURL('opportunityDetail', { opportunity });
        
        try {
            const apps = await api.getApplicationsForOpportunity(opportunity.id);
            setViewingOpportunity(prev => prev && prev.id === opportunity.id ? { ...prev, applications: apps } : prev);
        } catch (e) {}
    }, [view, updateURL]);

    const handleViewBand = useCallback(async (band: Band) => {
        setNavigationOrigin(view);
        setViewingBand(band);
        setView('bandPage');
        setIsMobileMenuOpen(false);
        updateURL('bandPage', { band });

        try {
            const fullBand = await api.getBandById(band.id);
            setViewingBand(prev => prev && prev.id === band.id ? fullBand : prev);
        } catch (e) {}
    }, [view, updateURL]);

    const handleViewFanList = useCallback((owner: User | Band) => {
        setNavigationOrigin(view);
        setViewingFanListOwner(owner);
        setView('fanList');
    }, [view]);

    const handleViewEndorserList = useCallback((owner: User) => {
        setNavigationOrigin(view);
        setViewingEndorserListOwner(owner);
        setView('endorserList');
    }, [view]);

    const handleClaimBand = useCallback(async (bandId: string) => {
        try {
            const updatedBand = await api.claimBand(bandId);
            setBands(prev => prev.map(b => b.id === bandId ? updatedBand : b));
            setViewingBand(updatedBand);
            showToast('Band page claimed successfully!');
        } catch (error: any) {
            showToast(`Error: ${error.message || 'Claim failed'}`);
        }
    }, [showToast]);

    const handleToggleBandFan = useCallback(async (bandId: string) => {
        if (authStatus !== 'authenticated') return;
        try {
            await api.toggleBandFan(bandId);
            await refreshBands();
            showToast("Fan status updated!");
        } catch (error) {
            showToast("Failed to update fan status.");
        }
    }, [authStatus, refreshBands, showToast]);

    const handleViewApplicantsForOpportunity = useCallback((opportunity: Opportunity) => { setNavigationOrigin(view); setViewingOpportunity(opportunity); setView('applicantList'); updateURL('applicantList', { opportunity }); }, [view, updateURL]);
    const handleViewApplication = useCallback((application: Application, opportunity: Opportunity) => { setNavigationOrigin(view); setViewingApplication(application); setViewingOpportunity(opportunity); setView('applicationDetail'); updateURL('applicationDetail', { opportunity }); }, [view, updateURL]);
    const handleEditOpportunity = useCallback((opportunity: Opportunity) => { setNavigationOrigin(view); setViewingOpportunity(opportunity); setView('editOpportunity'); updateURL('editOpportunity', { opportunity }); }, [view, updateURL]);
    const handleViewLineupEditor = useCallback((opportunity: Opportunity) => { setNavigationOrigin(view); setViewingOpportunity(opportunity); setView('lineupEditor'); updateURL('lineupEditor', { opportunity }); }, [view, updateURL]);
    const handleViewFlyer = useCallback((lineup: Partial<Lineup>, opportunity: Opportunity) => { setNavigationOrigin(view); setViewingLineup(lineup); setViewingOpportunity(opportunity); setView('flyerPreview'); }, [view]);

    const contextValue: NavigationContextState = useMemo(() => ({
      view, isMobileMenuOpen, searchTerm, setSearchTerm, viewingProfile, viewingOpportunity, setViewingOpportunity, viewingApplication,
      viewingBand, viewingLineup, viewingFanListOwner, viewingEndorserListOwner,
      bands, hasMoreBands, isLoadingMoreBands, loadMoreBands, networkTab, navigationOrigin, setIsMobileMenuOpen,
      handleNavigation, handleViewProfile, handleViewOpportunity, handleViewApplication,
      handleViewApplicantsForOpportunity, handleEditOpportunity,
      handleViewBand, handleViewFanList, handleViewEndorserList, handleClaimBand, handleToggleBandFan,
      refreshBands, handleViewLineupEditor, handleViewFlyer
    }), [
      view, isMobileMenuOpen, searchTerm, viewingProfile, viewingOpportunity, viewingApplication,
      viewingBand, viewingLineup, viewingFanListOwner, viewingEndorserListOwner,
      bands, hasMoreBands, isLoadingMoreBands, loadMoreBands, networkTab, navigationOrigin,
      handleNavigation, handleViewProfile, handleViewOpportunity, handleViewApplication,
      handleViewApplicantsForOpportunity, handleEditOpportunity, handleViewBand, handleViewFanList,
      handleViewEndorserList, handleClaimBand, handleToggleBandFan, refreshBands, handleViewLineupEditor, handleViewFlyer
    ]);

    return (
        <NavigationContext.Provider value={contextValue}>
            {children}
        </NavigationContext.Provider>
    );
}
